import _ from 'lodash';
import { DateTime, Duration, ToRelativeOptions } from 'luxon';
import formatters from './formatters';
import { join } from './strings';
import { isBankHoliday } from './holidays';

export type DateRange = {
    minDate?: DateTime | undefined;
    maxDate?: DateTime | undefined;
};

export const setZone = (date: DateTime): DateTime =>
    date.setZone('America/Sao_Paulo');

const getTitleCaseMonth = (date: DateTime): string => {
    return formatters.titleCase(date.monthShort.slice(0, 3));
};

const getTitleCaseMonthLong = (date: DateTime): string => {
    return formatters.titleCase(date.monthLong);
};

export const getMonthChoices = (
    beginDate: DateTime,
    endDate: DateTime,
    dayOfMonth: number = 1
): DateTime[] => {
    const beginMonth = beginDate.startOf('month');

    const endMonth = endDate.startOf('month');
    const diff = endMonth.diff(beginMonth, ['months']);
    return _.range(diff.months + 1).map(i => {
        const month = beginMonth.plus({ months: i });
        const lastDay = month.endOf('month').day;
        return month.set({ day: Math.min(dayOfMonth, lastDay) });
    });
};

export const toRelative = (
    date: DateTime,
    prefix?: string,
    pluralPrefix?: string,
    options?: ToRelativeOptions
): string | null => {
    const relative = date.toRelative(options);
    if (relative && typeof prefix === 'string') {
        const withoutPrefix = relative.split(' ').slice(1);
        const number = parseInt(withoutPrefix[0]);
        const formattedPrefix = formatters.numberInflection(
            number,
            prefix,
            pluralPrefix ?? prefix
        );
        return isNaN(number)
            ? relative
            : join([formattedPrefix, ...withoutPrefix]);
    }
    return relative;
};

export const toReadableDayMonth = (date: DateTime): string => {
    const localized = date.equals(date.startOf('day')) ? date : setZone(date);
    return localized.toFormat(`dd '${getTitleCaseMonth(localized)}'`);
};

export const toReadableDayMonthLong = (date: DateTime): string => {
    const localized = date.equals(date.startOf('day')) ? date : setZone(date);
    const day = localized.toFormat('dd');
    const month = getTitleCaseMonthLong(localized);
    return `${day} de ${month}`;
};

export const toReadableDate = (date: DateTime): string => {
    const localized = date.equals(date.startOf('day')) ? date : setZone(date);
    return localized.toFormat(`dd '${getTitleCaseMonth(localized)}' yyyy`);
};

export const toReadableDateShort = (date: DateTime): string =>
    date.toLocaleString(DateTime.DATE_SHORT);

export const toReadableDateLong = (date: DateTime): string => {
    const localized = date.equals(date.startOf('day')) ? date : setZone(date);
    return localized.toLocaleString(DateTime.DATE_FULL);
};

export const toReadableMonth = (date: DateTime): string => {
    return date.toFormat(`'${getTitleCaseMonth(date)}'/yyyy`);
};

export const toReadableMonthShort = (date: DateTime): string => {
    return date.toFormat(`'${getTitleCaseMonth(date)}' yy`);
};

export const toReadableMonthLong = (date: DateTime): string => {
    return date.toFormat(`'${getTitleCaseMonthLong(date)}' 'de' yyyy`);
};

export const getMonth = (date: DateTime): string => date.monthLong;

export const toTimestamp = (date: DateTime): string => {
    const localized = setZone(date);
    return localized.toFormat(`dd/MM/yyyy 'às' HH:mm`);
};

export const toReadableTimestamp = (date: DateTime): string => {
    const localized = setZone(date);
    return localized.toFormat(
        `dd '${getTitleCaseMonth(localized)}' yyyy 'às' HH:mm`
    );
};

export const toReadableTimestampLong = (date: DateTime): string => {
    const localized = setZone(date);
    return localized.toFormat(
        `dd 'de ${getTitleCaseMonthLong(localized)} de' yyyy 'às' HH:mm`
    );
};

export const toExpirationDate = (date: DateTime): string => {
    return date.toFormat(`MM/yyyy`);
};

export const toDateShort = (date: DateTime): string =>
    date.toFormat('dd/MM/yyyy');

export const toDbDate = (date: DateTime): string => date.toISODate();

export const fromDateShort = (date: string): DateTime => {
    return DateTime.fromFormat(date, 'dd/MM/yyyy');
};

export const fromDateTimeShort = (dateTime: string): DateTime => {
    return DateTime.fromFormat(dateTime, 'dd/MM/yyyy HH:mm');
};

export const toDateTimeShort = (dateTime: DateTime): string => {
    return dateTime.toFormat('dd/MM/yyyy HH:mm');
};

export const toISOValidity = (date: string): string =>
    DateTime.fromFormat(date, 'MM/yyyy').toFormat('yyyy-MM');

const weekdays: Record<number, string> = {
    1: 'segunda-feira',
    2: 'terça-feira',
    3: 'quarta-feira',
    4: 'quinta-feira',
    5: 'sexta-feira',
    6: 'sábado',
    7: 'domingo',
};

export const weekday = (date: DateTime): string => {
    return weekdays[date.weekday];
};

export const daysToNextMonth = (): number => {
    const now = DateTime.now();
    const nextMonth = now.plus({ months: 1 });
    return Math.ceil(
        DateTime.local(nextMonth.year, nextMonth.month, 1).diff(now, ['days'])
            .days
    );
};

export const isPast = (date: DateTime, oneSecPlus: boolean = true): boolean => {
    // using seconds instead of milliseconds to facilitate testing
    const now = DateTime.utc();
    const diff = now.diff(date.toUTC(), ['seconds']);

    return oneSecPlus ? Math.floor(diff.seconds) > 0 : diff.seconds > 0;
};

export const isDatePast = (date: DateTime): boolean => {
    const now = DateTime.utc();
    const diff = now.diff(date.toUTC(), ['day']);
    return Math.floor(diff.days) > 0;
};

export const toReadableDateRange = (init: DateTime, end: DateTime): string => {
    return formatters.range({
        min: init,
        max: end,
        formatter: toReadableDate,
        style: 'until',
    });
};

export const monthDifference = (start: DateTime, finish: DateTime): number => {
    return finish.diff(start, 'months').months;
};

export const daysDifference = (start: DateTime, finish: DateTime): number => {
    return Math.floor(finish.diff(start, 'days').days);
};

export const dateTimeDifference = (
    start: DateTime,
    finish: DateTime
): Duration => {
    const diff = finish.diff(start, ['days', 'hours']);
    const days = Math.max(0, diff.days);
    const hours = Math.max(0, diff.hours);

    return Duration.fromObject({ days, hours });
};
export const addMonthsToDate = (start: DateTime, months: number): DateTime => {
    return start.plus({ months });
};

export const nextDayDate = (day: number): DateTime => {
    const now = DateTime.now();
    const thisMonth = now.set({ day: day });
    const plusMonth = thisMonth.startOf('day') < DateTime.now().startOf('day');

    return plusMonth ? thisMonth.plus({ month: 1 }) : thisMonth;
};

export const isBusinessDay = (dt: DateTime) => {
    return dt.weekday < 6 && !isBankHoliday(dt);
};

export const isBusinessHours = (dt: DateTime) => {
    return dt.hour >= 9 && dt.hour <= 18;
};

const nextBusinessDay = (dt: DateTime) => {
    let date = dt;
    while (!isBusinessDay(date)) date = date.plus({ days: 1 });
    return date;
};

export const tedProcessingFinishDate = (startedAt: DateTime) => {
    let baseDate =
        startedAt.hour >= 16 ? startedAt.plus({ days: 1 }) : startedAt;

    baseDate = nextBusinessDay(baseDate);
    baseDate = baseDate.plus({ days: 1 });

    if (
        isBankHoliday(baseDate) ||
        baseDate.weekday === 1 ||
        baseDate.weekday === 7
    )
        baseDate = baseDate.plus({ days: isBankHoliday(baseDate) ? 2 : 1 });

    return baseDate.set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
};

export const toGreaterDetailRelative = (date: DateTime) => {
    const localized = setZone(date);
    const now = DateTime.now();
    const daysDiff = daysDifference(
        localized.startOf('day'),
        now.startOf('day')
    );

    if (daysDiff === 0) return localized.toFormat('HH:mm');
    if (daysDiff === 1) return 'Ontem';
    if (daysDiff < 7) return formatters.titleCase(weekday(localized));

    return toReadableDateShort(localized);
};

export const toMessageDatetime = (date: DateTime) => {
    const localized = setZone(date);
    return localized.toFormat('dd/MM - HH:mm');
};

export function ellapsed(a: DateTime) {
    return Math.ceil(DateTime.now().diff(a, 'seconds').seconds);
}
